import { useState, useEffect, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { AppContext, SttTopControls, SttTable } from '../../../sporttia/all';
import ActivityClassDialog from '../activities/ActivityClassDialog';

type Class = {
    id: number;
    name: string;
    facility: unknown;
    ini: string;
    end: string;
};

type ClassesResponse = {
    rows: Class[];
    count: number;
    page: number;
    pages: number;
};

type Props = {
    idTeacher: string | number | null;
};

export default function TeacherClasses({
    idTeacher,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [classes, setClasses] = useState<ClassesResponse>();
    const [filters, setFilters] = useState<{ name: string }>({ name: '' });
    const [classOpened, setClassOpened] = useState<Class | null>(null);

    /**
     * Load classes
     */
    const loadClasses = (p = { rows: 20, page: 1 }): void => {
        cxt?.api('GET', `/teachers/${idTeacher}/classes`, {
            params: {
                ...filters,
                ...p,
                page: p.page,
                rows: p.rows,
            },
            success: (r: ClassesResponse) => {
                setClasses(r);
            },
        });
    };

    /**
     * Teacher id changes
     */
    useEffect(() => {
        if (idTeacher) loadClasses();
    }, [idTeacher]);

    // -----| Render |-----
    return (
        <>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            name: 'name',
                            type: 'text',
                            value: filters.name,
                            onEnterKey: () => loadClasses(),
                        },
                    ] as any
                }
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    setFilters({ ...filters, [name]: value as string });
                }}
                onFilter={() => loadClasses()}
            />

            <SttTable
                data={classes}
                loading={false}
                autoload={false}
                onFetch={loadClasses}
                columns={[
                    {
                        title: t('Name'),
                        name: 'name',
                        field: 'name',
                        type: 'link',
                        onClick: (row: Class) => setClassOpened(row),
                    },
                    {
                        title: t('Facility'),
                        name: 'facility',
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        title: t('Day'),
                        name: 'day',
                        value: (row: Class) =>
                            format(parseISO(row.ini), 'EEE', {
                                locale: getDateLocale(),
                            }),
                    },
                    {
                        title: t('Date'),
                        name: 'date',
                        type: 'dmy',
                        field: 'ini',
                    },
                    {
                        title: t('Time'),
                        name: 'time',
                        type: 'periodTime',
                        value: (row: Class) => ({ ini: row.ini, end: row.end }),
                    },
                ]}
            />

            <ActivityClassDialog
                open={classOpened !== null}
                item={classOpened as any}
                onSave={() => loadClasses()}
                onClose={() => setClassOpened(null)}
            />
        </>
    );
}
